import React from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { Error, FileWithPreview, formatDate, FullScreenModal, HeaderLogo, TextAreaInput, TextInput, TicketModalProps } from "../../../libs";
import Select from "react-select";
import * as Styled from "./TicketModal.styled";
import { AttachmentUpload } from "../support-ticket-image";
import { AttachmentsDisplay } from "../support-ticket-images";

export const TicketModal: React.FC<TicketModalProps> = ({ show, onHide, content, ticket, ticketReplies, isAdmin, formik, setUploadedImages, uploadedImages, fetchPriorities, fetchStatuses, fetchTicketTypes, percentage }) => {

    const handleFilesSelected = (files: FileWithPreview[]) => {
        setUploadedImages((prevImages) => [...prevImages, ...files]);
    };

    const handleRemoveImage = (index: number) => {
        setUploadedImages((prevImages) => {
            URL.revokeObjectURL(prevImages[index].previewUrl!);
            return prevImages.filter((_, i) => i !== index);
        });
    };

    const closeTicket = () => {
        const closedStatus = fetchStatuses.find(option => option.label.props.children[1] === "Closed")?.value;
        if (formik.values.currentStatus === String(closedStatus)) {
            formik.setFieldValue("currentStatus",  'New');
        } else {
            formik.setFieldValue("currentStatus", 'Closed');
        }

        formik?.handleSubmit();
    };

    return (
        <FullScreenModal
            show={show}
            onHide={onHide}
            header={
                <Styled.ModalHeader closeButton closeVariant="white">
                    <HeaderLogo />
                </Styled.ModalHeader>
            }
            body={
                content === "add" ?
                    <Styled.ModalBody>
                        <Styled.BigTitle>{isAdmin ? "New Ticket" : "Submit Support Ticket"}</Styled.BigTitle>
                        <Styled.Title>{isAdmin ? "Create Ticket Manually" : "Create Quick Ticket"}</Styled.Title>
                        <Styled.Subtitle>
                            {isAdmin ? "Write and address new queries and issues" : "Write and address new queries and issues. The Cemboo support team is here to help you!"}
                        </Styled.Subtitle>

                        {/* First Row: Email (only for Admin) + Dropdowns */}
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <Row className="mb-4">
                                {isAdmin && (
                                    <Col md={3}>
                                        <Styled.Label>Email</Styled.Label>
                                        <TextInput
                                            fieldName="email"
                                            placeholder="Type Email"
                                            fieldValue={formik.values.email}
                                            onChangeHandler={formik.handleChange}
                                            onBlurHandler={formik.handleBlur}
                                            hasError={formik.touched.email && !!formik.errors.email}
                                        />
                                        {formik.touched.email && formik.errors.email && <Error message={formik.errors.email} />}
                                    </Col>

                                )}
                                <Col md={isAdmin ? 3 : 3}>
                                    <Styled.Label>Request Ticket Type</Styled.Label>
                                    <Select
                                        className="custom-select"
                                        styles={Styled.selectStyles}
                                        placeholder="Choose Type"
                                        options={fetchTicketTypes}
                                        isClearable
                                        value={fetchTicketTypes.find(option => option.value === Number(formik.values.ticketType))}
                                        onChange={(selectedOption) => formik.setFieldValue("ticketType", selectedOption?.value || "")}
                                    />
                                    {formik.touched.ticketType && formik.errors.ticketType && <Error message={formik.errors.ticketType} />}
                                </Col>
                                {isAdmin && (
                                    <Col md={3}>
                                        <Styled.Label>Current Status</Styled.Label>
                                        <Select
                                            className="custom-select"
                                            styles={Styled.selectStyles}
                                            placeholder="Select Status"
                                            options={fetchStatuses?.filter((e) => e.value !== null && e.forAdmin)}
                                            isClearable
                                            value={fetchStatuses.find(option => option.value === Number(formik.values.currentStatus))}
                                            onChange={(selectedOption) => formik.setFieldValue("currentStatus", selectedOption?.value || "")}
                                        />
                                        {formik.touched.currentStatus && formik.errors.currentStatus && <Error message={formik.errors.currentStatus} />}
                                    </Col>
                                )}
                                <Col md={isAdmin ? 3 : 3}>
                                    <Styled.Label>Priority</Styled.Label>
                                    <Select
                                        className="custom-select"
                                        styles={Styled.selectStyles}
                                        placeholder="Select Priority"
                                        options={fetchPriorities}
                                        isClearable
                                        value={fetchPriorities.find(option => option.value === Number(formik.values.priority))}
                                        onChange={(selectedOption) => formik.setFieldValue("priority", selectedOption?.value || "")}
                                    />

                                    {formik.touched.priority && formik.errors.priority && <Error message={formik.errors.priority} />}
                                </Col>
                            </Row>

                            {/* Second Row: Ticket Title + Dropdown */}
                            <Row className="mb-4">
                                <Col >
                                    <Styled.Label>Ticket Title</Styled.Label>
                                    <TextInput
                                        fieldName="title"
                                        placeholder="Type ticket title here..."
                                        fieldValue={formik.values.title}
                                        onChangeHandler={formik.handleChange}
                                        onBlurHandler={formik.handleBlur}
                                        hasError={formik.touched.title && !!formik.errors.title}
                                    />
                                    {formik.touched.title && formik.errors.title && <Error message={formik.errors.title} />}
                                </Col>

                            </Row>

                            {/* Ticket Body */}
                            <Styled.Label>Ticket Body</Styled.Label>
                            <TextAreaInput
                                fieldName="message"
                                placeholder="Describe the issue here..."
                                rows={4}
                                fieldValue={formik.values.message}
                                onChangeHandler={formik.handleChange}
                                onBlurHandler={formik.handleBlur}
                            />
                            <Styled.Footer>
                                {uploadedImages.length > 0 && (
                                    <Styled.AttachmentsWrapper>
                                        {uploadedImages.map((image, index) => (
                                            <Styled.AttachmentContainer key={index}>
                                                <Styled.AttachmentImage src={image.previewUrl} alt={`Attachment ${index + 1}`} />
                                                <Styled.RemoveIcon onClick={() => handleRemoveImage(index)}>X</Styled.RemoveIcon>
                                            </Styled.AttachmentContainer>
                                        ))}
                                    </Styled.AttachmentsWrapper>
                                )}

                                {/* Buttons Section */}
                                <Styled.ButtonsContainer>
                                    <AttachmentUpload onFilesSelected={handleFilesSelected} buttonText="Add Attachments" />
                                    <Styled.FixedWidthButton
                                        variant="primary"
                                        onClick={() => formik?.handleSubmit()}
                                        disabled={formik.isSubmitting}
                                    >
                                        {formik.isSubmitting && (
                                            uploadedImages.length > 0 ? (
                                                <>
                                                    <Styled.LoadingSpinner animation="border" size="sm" variant="primary" />
                                                    {percentage}% Uploaded&nbsp;
                                                </>
                                            ) : (
                                                <>
                                                    <Styled.LoadingSpinner animation="border" size="sm" variant="primary" />
                                                    Processing...
                                                </>
                                            )
                                        )}

                                        {!formik.isSubmitting && (
                                            isAdmin ? "Create Ticket" : "Send Ticket"
                                        )}
                                    </Styled.FixedWidthButton>

                                </Styled.ButtonsContainer>
                            </Styled.Footer>
                        </form>
                    </Styled.ModalBody>
                    :
                    <Styled.ModalBody>
                        <Styled.TicketHeader>
                            <Styled.TicketInfoWrapper>
                                <Styled.PriorityDot style={{ backgroundColor: ticket?.priority.color }} />
                                <Styled.TicketId>{`Ticket #${ticket?.ticketId}`}</Styled.TicketId>
                                <Styled.TicketId>{ticket?.priority.name}</Styled.TicketId>
                            </Styled.TicketInfoWrapper>
                            <Styled.CloseTicketButton
                                status={formik.values.currentStatus}
                                onClick={closeTicket}
                            >
                                {formik.values.currentStatus === String(fetchStatuses.find(option => option.label.props.children[1] === "Closed")?.value)
                                    ? "Re-open Ticket"
                                    : "Close Ticket"}
                            </Styled.CloseTicketButton>


                        </Styled.TicketHeader>
                        <Styled.PostedAt>{formatDate(ticket?.createdAt)}</Styled.PostedAt>


                        <Styled.TicketWrapper>
                            <Row className="align-items-center mb-3">
                                <Col>
                                    <Styled.FlexRow>
                                        <Styled.Label>{ticket?.title}</Styled.Label>
                                        <Styled.PriorityTag>{ticket?.priority.name}</Styled.PriorityTag>
                                    </Styled.FlexRow>
                                </Col>
                            </Row>
                            <Styled.TicketDescription>{ticket?.message}</Styled.TicketDescription>

                            <Styled.AttachmentsWrapper>
                                <AttachmentsDisplay
                                    attachments={(ticket?.attachments?.slice(0, 4) || []).map(attachment => ({
                                        url: attachment.url || ""
                                    }))}
                                    uploadedImages={[]}
                                />
                            </Styled.AttachmentsWrapper>

                            {/* Replies Section */}
                            <Styled.RepliesWrapper>
                                <Styled.RepliesTitle>Replies ({ticketReplies?.length || 0})</Styled.RepliesTitle>

                                {ticketReplies?.map((reply, index) => (
                                    <Styled.ReplyItem key={index}>
                                        <Styled.ReplyHeader>
                                            <Styled.ReplyTitle>
                                                {reply.user.name === "Support Admin" ? "Support Admin" : reply.user.name}
                                            </Styled.ReplyTitle>
                                            <Styled.ReplyDate>{formatDate(reply.createdAt)}</Styled.ReplyDate>
                                        </Styled.ReplyHeader>

                                        <Styled.ReplySubtitle>
                                            {reply.message}
                                        </Styled.ReplySubtitle>
                                        <Styled.AttachmentsWrapper>
                                            <AttachmentsDisplay
                                                attachments={(reply?.attachments?.slice(0, 4) || []).map(attachment => ({
                                                    url: attachment.url || ""
                                                }))}
                                                uploadedImages={[]}
                                            />

                                        </Styled.AttachmentsWrapper>
                                    </Styled.ReplyItem>
                                ))}
                            </Styled.RepliesWrapper>
                        </Styled.TicketWrapper>

                        {/* Reply Section */}
                        <Styled.Wrapper>
                            <form onSubmit={formik.handleSubmit} noValidate>
                                <Styled.Title>Reply to Ticket</Styled.Title>
                                <div style={{ marginBottom: "20px" }}></div>

                                {/* Admin Dropdowns (Only Visible for Admins) */}
                                {isAdmin && (
                                    <Row className="mb-4">
                                        <Col md={3}>
                                            <Styled.Label>Request Ticket Type</Styled.Label>
                                            <Select
                                                className="custom-select"
                                                styles={Styled.selectStyles}
                                                placeholder="Choose Type"
                                                options={fetchTicketTypes}
                                                isClearable
                                                value={fetchTicketTypes.find(option => option.value === Number(formik.values.ticketType))}
                                                onChange={(selectedOption) => formik.setFieldValue("ticketType", selectedOption?.value || "")}
                                            />
                                            {formik.touched.ticketType && formik.errors.ticketType && <Error message={formik.errors.ticketType} />}
                                        </Col>
                                        <Col md={3}>
                                            <Styled.Label>Current Status</Styled.Label>
                                            <Select
                                                className="custom-select"
                                                styles={Styled.selectStyles}
                                                placeholder="Select Status"
                                                options={fetchStatuses?.filter((e) => e.value !== null && e.forAdmin)}
                                                isClearable
                                                value={fetchStatuses.find(option => option.value === Number(formik.values.currentStatus))}
                                                onChange={(selectedOption) => formik.setFieldValue("currentStatus", selectedOption?.value || "")}
                                                isOptionDisabled={(option) => option?.isDisabled ?? false}
                                            />

                                            {formik.touched.currentStatus && formik.errors.currentStatus && <Error message={formik.errors.currentStatus} />}
                                        </Col>
                                        <Col md={3}>
                                            <Styled.Label>Priority</Styled.Label>
                                            <Select
                                                className="custom-select"
                                                styles={Styled.selectStyles}
                                                placeholder="Select Priority"
                                                options={fetchPriorities}
                                                isClearable
                                                value={fetchPriorities.find(option => option.value === Number(formik.values.priority))}
                                                onChange={(selectedOption) => formik.setFieldValue("priority", selectedOption?.value || "")}
                                            />
                                            {formik.touched.priority && formik.errors.priority && <Error message={formik.errors.priority} />}
                                        </Col>
                                    </Row>
                                )}

                                <Styled.Label>Ticket Body</Styled.Label>
                                <TextAreaInput
                                    fieldName="message"
                                    placeholder="Describe the issue here..."
                                    rows={4}
                                    fieldValue={formik.values.message}
                                    onChangeHandler={formik.handleChange}
                                    onBlurHandler={formik.handleBlur}
                                />
                                <Styled.Footer>
                                    {/* Images Appear Above the Buttons */}
                                    {uploadedImages.length > 0 && (
                                        <AttachmentsDisplay
                                            attachments={[]}
                                            uploadedImages={uploadedImages.map((img) => ({
                                                previewUrl: img.previewUrl || ""
                                            }))}
                                            onRemoveImage={handleRemoveImage}
                                        />
                                    )}

                                    {/* Buttons Section */}
                                    <Styled.ButtonsContainer>
                                        <AttachmentUpload onFilesSelected={handleFilesSelected} buttonText="Add Attachments" />
                                        <Styled.FixedWidthButton
                                            variant="primary"
                                            onClick={() => formik?.handleSubmit()}
                                            disabled={formik.isSubmitting}
                                        >
                                            {formik.isSubmitting ? (
                                                <>
                                                    <Spinner animation="border" size="sm" variant="light" style={{ marginRight: "8px" }} />
                                                    Submitting...
                                                </>
                                            ) : (
                                                "Submit Reply"
                                            )}
                                        </Styled.FixedWidthButton>
                                    </Styled.ButtonsContainer>
                                </Styled.Footer>
                            </form>
                        </Styled.Wrapper>
                    </Styled.ModalBody>
            }
            children={undefined}
        />
    );
};
